import { useNewToast } from 'hooks/newToast';
import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
  useRef,
  createContext,
} from 'react';
import api from 'services';

const OpenFinanceContext = createContext({});

const STATUS_INICIAL = {
  contratado: false,
  status: 'inexistente',
  limiteContas: 4,
  conexoesConectadas: 0,
  contasConectadas: 0,
  cartoesConectados: 0,
  podeConectar: false,
};

const PAGINATION_INICIAL = { page: 1, pageSize: 20, total: 0 };

export function OpenFinanceProvider({ children }) {
  const { showToast } = useNewToast();

  const [status, setStatus] = useState(STATUS_INICIAL);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusCarregado, setStatusCarregado] = useState(false);

  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsCarregado, setItemsCarregado] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [cartoes, setCartoes] = useState([]);
  const [saldoTotal, setSaldoTotal] = useState(0);
  const [faturaTotal, setFaturaTotal] = useState(0);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsCarregado, setAccountsCarregado] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState(PAGINATION_INICIAL);
  const [naoConferidas, setNaoConferidas] = useState(0);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const [categorias, setCategorias] = useState([]);

  const [connectToken, setConnectToken] = useState(null);
  const [connectTokenLoading, setConnectTokenLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [desvincularLoading, setDesvincularLoading] = useState(false);
  const [conferenciaLoteLoading, setConferenciaLoteLoading] = useState(false);

  /**
   * Banner de vitrine e atalho para o módulo vivem em telas que montam os dois
   * ao mesmo tempo; sem isto, cada montagem dispararia seu próprio GET /status.
   * Compartilhar a promessa em voo faz a resposta chegar uma vez só para todos.
   */
  const statusEmVoo = useRef(null);

  /**
   * Único endpoint fora do gate: erro aqui não é excepcional (conta sem o
   * módulo cai em 403), então mantém o estado padrão e deixa a tela mostrar a
   * vitrine em vez de um toast de erro.
   */
  const getStatus = useCallback(async () => {
    if (statusEmVoo.current) return statusEmVoo.current;

    const busca = (async () => {
      try {
        setStatusLoading(true);
        const response = await api.openFinance().getStatus();
        setStatus(response);
        return response;
      } catch (error) {
        setStatus(STATUS_INICIAL);
        return STATUS_INICIAL;
      } finally {
        setStatusLoading(false);
        setStatusCarregado(true);
        statusEmVoo.current = null;
      }
    })();

    statusEmVoo.current = busca;

    return busca;
  }, []);

  const getItems = useCallback(async () => {
    try {
      setItemsLoading(true);
      const response = await api.openFinance().getItems();
      setItems(response || []);
    } catch (error) {
      setItems([]);
    } finally {
      setItemsLoading(false);
      setItemsCarregado(true);
    }
  }, []);

  const getAccounts = useCallback(async () => {
    try {
      setAccountsLoading(true);
      const response = await api.openFinance().getAccounts();
      setAccounts(response?.contas || []);
      setCartoes(response?.cartoes || []);
      setSaldoTotal(response?.saldoTotal || 0);
      setFaturaTotal(response?.faturaTotal || 0);
    } catch (error) {
      setAccounts([]);
      setCartoes([]);
      setSaldoTotal(0);
      setFaturaTotal(0);
    } finally {
      setAccountsLoading(false);
      setAccountsCarregado(true);
    }
  }, []);

  /**
   * O filtro do extrato mora na página, mas quem muda o conjunto de transações
   * no servidor (sincronizar, desconectar) é daqui. Guardamos o último filtro
   * usado para conseguir refazer a busca sem devolver esse estado para cá nem
   * recarregar a página inteira — que custaria o filtro digitado e a rolagem.
   */
  const ultimoFiltro = useRef(null);

  const getTransactions = useCallback(async (filter) => {
    ultimoFiltro.current = filter;

    try {
      setTransactionsLoading(true);
      const response = await api.openFinance().getTransactions(filter);
      setTransactions(response?.data || []);
      setPagination(response?.pagination || PAGINATION_INICIAL);
      setNaoConferidas(response?.naoConferidas || 0);
    } catch (error) {
      setTransactions([]);
      setPagination(PAGINATION_INICIAL);
      setNaoConferidas(0);
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  const getCategorias = useCallback(async () => {
    try {
      const response = await api.openFinance().getCategorias();
      setCategorias(response || []);
    } catch (error) {
      setCategorias([]);
    }
  }, []);

  /**
   * O backend recusa aqui quando já há 4 contas conectadas — a mensagem dele é
   * específica ("você já tem X de Y"), então repassamos em vez de genérica.
   */
  const createConnectToken = useCallback(
    async (itemId) => {
      try {
        setConnectTokenLoading(true);
        const response = await api.openFinance().createConnectToken(itemId);
        setConnectToken(response?.accessToken || null);
        return response?.accessToken || null;
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Não foi possível iniciar a conexão com o banco.',
        });
        return null;
      } finally {
        setConnectTokenLoading(false);
      }
    },
    [showToast],
  );

  const limparConnectToken = useCallback(() => setConnectToken(null), []);

  /**
   * Chamado quando o widget devolve o itemId. O backend grava o vínculo e
   * enfileira o primeiro sync — os dados chegam depois, por isso a tela mostra
   * "sincronizando" em vez de esperar aqui.
   */
  const createItem = useCallback(
    async (pluggyItemId) => {
      try {
        const response = await api.openFinance().createItem(pluggyItemId);
        showToast({
          type: 'success',
          message: 'Banco conectado! Estamos buscando seus dados.',
        });
        await getItems();
        await getStatus();
        return response;
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Não foi possível concluir a conexão com o banco.',
        });
        throw error;
      }
    },
    [showToast, getItems, getStatus],
  );

  const syncItem = useCallback(
    async (id) => {
      try {
        setSyncLoading(id);
        await api.openFinance().syncItem(id);
        showToast({ type: 'success', message: 'Dados atualizados!' });
        await getItems();
        await getAccounts();
        await getTransactions(ultimoFiltro.current);
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Não foi possível atualizar agora. Tente novamente em alguns minutos.',
        });
      } finally {
        setSyncLoading(null);
      }
    },
    [showToast, getItems, getAccounts, getTransactions],
  );

  /**
   * Desconectar encerra a cobrança na Pluggy. Se o backend falhar no DELETE ele
   * mantém o banco conectado de propósito (melhor aparecer na tela do que
   * sumir cobrando), então não removemos nada da lista sem recarregar.
   */
  const disconnectItem = useCallback(
    async (id) => {
      try {
        await api.openFinance().disconnectItem(id);
        showToast({ type: 'success', message: 'Banco desconectado.' });
        await getItems();
        await getAccounts();
        await getStatus();
        await getTransactions(ultimoFiltro.current);
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Não foi possível desconectar o banco agora.',
        });
        throw error;
      }
    },
    [showToast, getItems, getAccounts, getStatus, getTransactions],
  );

  const linkAccount = useCallback(
    async (id, gloFluxoId) => {
      try {
        await api.openFinance().linkAccount(id, gloFluxoId);
        setAccounts((old) =>
          old.map((item) =>
            item.id === id ? { ...item, gloFluxoId } : item,
          ),
        );
        showToast({ type: 'success', message: 'Conta vinculada!' });
      } catch (error) {
        showToast({
          type: 'error',
          message: 'Não foi possível vincular a conta.',
        });
      }
    },
    [showToast],
  );

  const importTransaction = useCallback(
    async (id, data) => {
      try {
        setImportLoading(true);
        const response = await api.openFinance().importTransaction(id, data);

        // Importado nasce conferido: o cliente já olhou e decidiu.
        setTransactions((old) =>
          old.map((item) =>
            item.id === id
              ? {
                  ...item,
                  conferido: true,
                  gloFinanceiroId: response?.gloFinanceiroId,
                }
              : item,
          ),
        );

        showToast({
          type: 'success',
          message: 'Lançamento criado no financeiro!',
        });
        return response;
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Não foi possível importar a movimentação.',
        });
        throw error;
      } finally {
        setImportLoading(false);
      }
    },
    [showToast],
  );

  const vincularTransaction = useCallback(
    async (id, gloFinanceiroId) => {
      try {
        setImportLoading(true);
        await api.openFinance().vincularTransaction(id, gloFinanceiroId);

        setTransactions((old) =>
          old.map((item) =>
            item.id === id
              ? { ...item, conferido: true, gloFinanceiroId }
              : item,
          ),
        );

        showToast({
          type: 'success',
          message: 'Movimentação vinculada ao lançamento!',
        });
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Não foi possível vincular a movimentação.',
        });
        throw error;
      } finally {
        setImportLoading(false);
      }
    },
    [showToast],
  );

  const desvincularTransaction = useCallback(
    async (id, excluirLancamento) => {
      try {
        setDesvincularLoading(true);
        await api.openFinance().desvincularTransaction(id, excluirLancamento);

        setTransactions((old) =>
          old.map((item) =>
            item.id === id
              ? { ...item, conferido: false, gloFinanceiroId: null }
              : item,
          ),
        );
        setNaoConferidas((old) => old + 1);

        showToast({
          type: 'success',
          message: excluirLancamento
            ? 'Vínculo desfeito e lançamento excluído.'
            : 'Movimentação desvinculada do lançamento.',
        });

        return true;
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Não foi possível desvincular a movimentação.',
        });
        return false;
      } finally {
        setDesvincularLoading(false);
      }
    },
    [showToast],
  );

  /** Otimista: marcar conferido é reversível e barato, esperar o servidor trava a lista. */
  const conferirTransaction = useCallback(
    async (id, conferido) => {
      setTransactions((old) =>
        old.map((item) => (item.id === id ? { ...item, conferido } : item)),
      );
      setNaoConferidas((old) => Math.max(0, old + (conferido ? -1 : 1)));

      try {
        await api.openFinance().conferirTransaction(id, conferido);
      } catch (error) {
        setTransactions((old) =>
          old.map((item) =>
            item.id === id ? { ...item, conferido: !conferido } : item,
          ),
        );
        setNaoConferidas((old) => Math.max(0, old + (conferido ? 1 : -1)));
        showToast({
          type: 'error',
          message: 'Não foi possível salvar a conferência.',
        });
      }
    },
    [showToast],
  );

  /**
   * Em lote não dá para ser otimista como no clique único: o cliente acabou de
   * mexer em dezenas de linhas de uma vez e precisa da confirmação de que o
   * servidor guardou — reverter tudo depois seria pior do que esperar.
   */
  const conferirTransactionsEmLote = useCallback(
    async (ids, conferido) => {
      if (!ids?.length) return false;

      // Delta antes do await: depois a lista pode ter mudado (sync/filtro) e o
      // closure de `transactions` ficaria obsoleto no setNaoConferidas.
      const selecionados = new Set(ids);
      const mudaram = transactions.filter(
        (item) => selecionados.has(item.id) && item.conferido !== conferido,
      ).length;

      try {
        setConferenciaLoteLoading(true);
        await api.openFinance().conferirTransactionsEmLote(ids, conferido);

        setTransactions((old) =>
          old.map((item) =>
            selecionados.has(item.id) ? { ...item, conferido } : item,
          ),
        );

        setNaoConferidas((old) =>
          Math.max(0, old + (conferido ? -mudaram : mudaram)),
        );

        showToast({
          type: 'success',
          message:
            ids.length === 1
              ? `Movimentação marcada como ${conferido ? 'conferida' : 'não conferida'}.`
              : `${ids.length} movimentações marcadas como ${
                  conferido ? 'conferidas' : 'não conferidas'
                }.`,
        });

        return true;
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Não foi possível salvar a conferência.',
        });
        return false;
      } finally {
        setConferenciaLoteLoading(false);
      }
    },
    [showToast, transactions],
  );

  const value = useMemo(
    () => ({
      status,
      statusLoading,
      statusCarregado,
      getStatus,
      items,
      itemsLoading,
      itemsCarregado,
      getItems,
      accounts,
      cartoes,
      saldoTotal,
      faturaTotal,
      accountsLoading,
      accountsCarregado,
      getAccounts,
      transactions,
      pagination,
      naoConferidas,
      transactionsLoading,
      getTransactions,
      categorias,
      getCategorias,
      connectToken,
      connectTokenLoading,
      createConnectToken,
      limparConnectToken,
      createItem,
      syncItem,
      syncLoading,
      disconnectItem,
      linkAccount,
      importTransaction,
      vincularTransaction,
      desvincularTransaction,
      desvincularLoading,
      conferirTransaction,
      conferirTransactionsEmLote,
      conferenciaLoteLoading,
      importLoading,
    }),
    [
      status,
      statusLoading,
      statusCarregado,
      getStatus,
      items,
      itemsLoading,
      itemsCarregado,
      getItems,
      accounts,
      cartoes,
      saldoTotal,
      faturaTotal,
      accountsLoading,
      accountsCarregado,
      getAccounts,
      transactions,
      pagination,
      naoConferidas,
      transactionsLoading,
      getTransactions,
      categorias,
      getCategorias,
      connectToken,
      connectTokenLoading,
      createConnectToken,
      limparConnectToken,
      createItem,
      syncItem,
      syncLoading,
      disconnectItem,
      linkAccount,
      importTransaction,
      vincularTransaction,
      desvincularTransaction,
      desvincularLoading,
      conferirTransaction,
      conferirTransactionsEmLote,
      conferenciaLoteLoading,
      importLoading,
    ],
  );

  return (
    <OpenFinanceContext.Provider value={value}>
      {children}
    </OpenFinanceContext.Provider>
  );
}

export function useOpenFinance() {
  const context = useContext(OpenFinanceContext);

  if (!context) {
    throw new Error('useOpenFinance must be used within an OpenFinanceProvider');
  }

  return context;
}
